import { UseQueryResult } from '@tanstack/react-query';

/**
 * Query render state discriminated union
 * Forces explicit handling of all query states
 */
export type QueryRenderState<TData, TError = Error> =
  | { status: 'loading'; data: undefined; error: null }
  | { status: 'error'; data: undefined; error: TError }
  | { status: 'empty'; data: TData; error: null }
  | { status: 'success'; data: TData; error: null };

/**
 * Converts a TanStack Query result into a discriminated union state
 * that forces consumers to handle all cases.
 * 
 * @param queryResult - The result from useQuery
 * @param isEmpty - Function to determine if data represents an empty state
 * @returns Discriminated union representing current query state
 * 
 * @example
 * const state = getQueryState(query, (data) => data.items.length === 0);
 * 
 * switch (state.status) {
 *   case 'loading': return <Loading />;
 *   case 'error': return <Error error={state.error} />;
 *   case 'empty': return <Empty />;
 *   case 'success': return <Data data={state.data} />;
 * }
 */
export function getQueryState<TData, TError = Error>(
  queryResult: UseQueryResult<TData, TError>,
  isEmpty?: (data: TData) => boolean
): QueryRenderState<TData, TError> {
  const { data, isLoading, isError, error, isFetching } = queryResult;

  // Loading: First load with no data yet
  if (isLoading && !data) {
    return { status: 'loading', data: undefined, error: null };
  }

  // Error: Query failed
  if (isError && error) {
    return { status: 'error', data: undefined, error };
  }

  // Empty: Data loaded but empty (if isEmpty checker provided)
  if (data && isEmpty && isEmpty(data)) {
    return { status: 'empty', data, error: null };
  }

  // Success: Data loaded
  if (data) {
    return { status: 'success', data, error: null };
  }

  // Fallback to loading if none of the above (should not happen)
  return { status: 'loading', data: undefined, error: null };
}

/**
 * Extended query result with render state helper
 * Provides both the raw query result and a typed render state
 */
export type QueryWithState<TData, TError = Error> = UseQueryResult<TData, TError> & {
  /**
   * Discriminated union state for explicit rendering logic.
   * Use this in components to ensure all states are handled.
   */
  renderState: QueryRenderState<TData, TError>;
};
